/**
 * Homeowner Onboarding Flow
 * Welcome, address, home questions, then the complete home profile.
 * Complete profile adds items on one short screen.
 */
import React, { useState, useEffect, useRef } from 'react';
import { homeownerRecords, updateHomeownerClient, homeownerPost, homeownerTasks } from '../utils/homeownerApi';
import { writeSetupResume } from '../utils/homeownerRestore';
import HomeownerPlanStep from './HomeownerPlanStep';
import { reassignTasks } from '../utils/taskAssignment';
import { canonicalPropertyType, featuresFromHomeAnswers, propertyTypeChoices, toggleFeature } from '../shared/homeownerIntake';
import { featuresFromFacts, homeFactsPatch } from '../shared/homeFacts';
import HomeFactsFields from './HomeFactsFields';
import { homeProfileSavePatch, readHomeProfile } from '../shared/homeProfile';
import HomeProfileQuestions from './HomeProfileQuestions';
import { useTheme } from '../ThemeContext';
import { buildSystemData, INVENTORY_TYPES } from '../shared/inventoryItem';
import { contentTypeForFile, storageErrorMessage } from '../utils/fileUpload';
import InventoryItemForm from './InventoryItemForm';
import SetupScreen from './SetupScreen';
import { clearToasts, showToast } from './Toast';

function isPlainPlan(value) {
  if (!value || typeof value !== 'object') return false;
  // A button click passes a React event. Copying it into the save body makes
  // JSON.stringify throw, so the setup request never reaches Supabase.
  if (typeof value.preventDefault === 'function') return false;
  if (value.nativeEvent || value._reactName) return false;
  return true;
}

const HomeownerOnboarding = ({ client, onComplete, onCancel, onLogout, startAtSystemsStep = false, startAtItemForm = false, resumeSetup = false }) => {
  const { colors } = useTheme();
  const [step, setStep] = useState(() => {
    if (startAtSystemsStep || startAtItemForm) return 4;
    if (resumeSetup && client.address) return 2;
    return 1;
  }); // 1: welcome, 2: address and home questions, 4: inventory, 5: plan
  const [setupType, setSetupType] = useState(startAtSystemsStep || startAtItemForm ? 'robust' : null);
  const [loading, setLoading] = useState(false);
  const [showHomeQuestions, setShowHomeQuestions] = useState(false);

  // Form data. Home answers use the same columns as Property Details.
  const [propertyData, setPropertyData] = useState(() => Object.assign({
    address: client.address || '',
    property_type: canonicalPropertyType(client.property_type) || 'single_family',
    features: client.features || [],
    year_built: client.year_built || '',
    square_feet: client.square_feet || '',
  }, readHomeProfile(client)));

  const [systemsData, setSystemsData] = useState({});
  const [tasksReady, setTasksReady] = useState(false);
  const [inventoryEditor, setInventoryEditor] = useState(() => (
    startAtSystemsStep ? null : { systemType: '', data: null }
  ));

  const brandColor = '#59c4c4';
  const stepKey = `${step}:${showHomeQuestions}`;
  const seenStep = useRef(stepKey);

  useEffect(() => {
    let cancel = false;
    if (typeof homeownerTasks !== 'function') return undefined;
    const pending = homeownerTasks('');
    if (!pending || typeof pending.then !== 'function') return undefined;
    pending
      .then((data) => {
        const rows = data && (data.data || data.tasks);
        if (!cancel) setTasksReady(Array.isArray(rows) && rows.length > 0);
      })
      .catch(() => {
        if (!cancel) setTasksReady(false);
      });
    return () => {
      cancel = true;
    };
  }, [client.id]);

  useEffect(() => {
    if (seenStep.current === stepKey) return;
    seenStep.current = stepKey;
    clearToasts();
  }, [stepKey]);

  useEffect(() => {
    if (startAtSystemsStep || !client || !client.id) return;
    writeSetupResume(window.localStorage, client.id, true);
  }, [client, startAtSystemsStep]);

  // Load existing client_systems data on mount
  useEffect(() => {
    const loadExistingSystems = async () => {
      try {
        const { data } = await homeownerRecords({ resource: 'client_systems', action: 'list' });

        if (data && data.length > 0) {
          const existingData = {};
          data.forEach(system => {
            existingData[system.system_type] = system.system_data;
          });
          setSystemsData(existingData);
        }
      } catch (error) {
        console.error('Error loading systems:', error);
      }
    };

    loadExistingSystems();
  }, [client.id]);

  const propertyTypes = propertyTypeChoices();

  const handleFeatureToggle = (featureId) => {
    setPropertyData(prev => ({
      ...prev,
      features: toggleFeature(prev.features, featureId)
    }));
  };

  const answeredFeatures = (systems) => featuresFromHomeAnswers({
    features: propertyData.features,
    systems: systems || systemsData,
    sewerType: propertyData.sewer_type,
    waterSource: propertyData.water_source,
  });

  const savedHomeFields = () => Object.assign({
    address: propertyData.address,
    property_type: canonicalPropertyType(propertyData.property_type) || propertyData.property_type,
  }, homeProfileSavePatch(propertyData));

  const syncTasks = async (features) => {
    try {
      await reassignTasks(
        client.id,
        client.agent_id,
        propertyData.property_type,
        features
      );
    } catch (error) {
      console.error('Error syncing tasks from home features:', error);
    }
  };

  const saveRobustSetup = async (planFields, systemsOverride, options) => {
    const systems = systemsOverride || systemsData;
    const skipped = Boolean(options && options.skipped);
    setLoading(true);
    try {
      // Save basic property data
      const fromAnswers = answeredFeatures(systems);
      const features = featuresFromFacts(fromAnswers, propertyData);
      if (fromAnswers.indexOf('well') !== -1 && features.indexOf('well') === -1) features.push('well');
      const plan = isPlainPlan(planFields)
        ? planFields
        : { onboarding_completed_at: new Date().toISOString() };
      const updateData = Object.assign(savedHomeFields(), homeFactsPatch(propertyData), {
        features,
      }, plan);

      // Only mark onboarding complete if this is initial onboarding, not adding systems later
      if (!startAtSystemsStep) {
        updateData.onboarding_completed = true;
      }

      await updateHomeownerClient(updateData);

      // Save systems data to new table
      const systemsToSave = Object.entries(systems).map(([systemId, data]) => ({
        client_id: client.id,
        agent_id: client.agent_id,
        system_type: systemId,
        system_data: data
      }));

      if (systemsToSave.length > 0) {
        await homeownerRecords({
          resource: 'client_systems',
          action: 'upsert',
          records: systemsToSave
        });
      }

      await syncTasks(features);
      if (!startAtSystemsStep) {
        writeSetupResume(window.localStorage, client.id, false);
        try {
          await homeownerPost('/.netlify/functions/homeowner-plan', { action: 'apply-answers' });
        } catch (planError) {
          console.error('Plan adjustments were not applied yet', planError);
        }
      }

      const itemCount = systemsToSave.length;
      let message = 'Setup complete.';
      if (startAtSystemsStep) {
        if (!itemCount) message = 'Saved.';
        else message = itemCount === 1 ? 'Saved 1 item.' : `Saved ${itemCount} items.`;
      } else if (skipped) {
        message = 'Skipped for now. You can add items later.';
      } else if (!itemCount) {
        message = 'Setup complete. You can add items later.';
      }
      showToast(message, 'success');
      if (onComplete) onComplete({ skipped, items: itemCount });
    } catch (error) {
      console.error('Error saving robust setup:', error);
      showToast('Error saving setup. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
    return true;
  };

  const uploadInventoryPhoto = async (file, systemType) => {
    const contentType = contentTypeForFile(file);
    const upload = await homeownerRecords({
      resource: 'client_documents',
      action: 'upload_url',
      fileName: file.name || `${systemType}.jpg`,
    });
    const uploadResponse = await fetch(upload.signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    if (!uploadResponse.ok) throw new Error(await storageErrorMessage(uploadResponse));
    const inserted = await homeownerRecords({
      resource: 'client_documents',
      action: 'insert',
      record: {
        title: 'Item photo',
        category: 'Other',
        file_name: file.name,
        file_path: upload.path,
        file_type: contentType,
        mime_type: contentType,
        file_size: file.size,
        document_type: 'Other',
      },
    });
    return {
      path: upload.path,
      name: file.name,
      documentId: inserted && inserted.data && inserted.data.id,
    };
  };

  const saveInventoryItem = async (form) => {
    if (!form.systemType) return;
    setLoading(true);
    try {
      let photo = null;
      if (form.photoFile) {
        try {
          photo = await uploadInventoryPhoto(form.photoFile, form.systemType);
        } catch (error) {
          console.error('Error uploading item photo:', error);
          showToast('The item will save. The photo did not upload.');
        }
      }
      const existing = systemsData[form.systemType] || {};
      const payload = buildSystemData(existing, { ...form, photo }, new Date().getFullYear());
      await homeownerRecords({
        resource: 'client_systems',
        action: 'upsert',
        record: {
          client_id: client.id,
          agent_id: client.agent_id,
          system_type: form.systemType,
          system_data: payload,
        },
      });
      const nextSystems = { ...systemsData, [form.systemType]: payload };
      setSystemsData(nextSystems);
      if (inventoryEditor && inventoryEditor.systemType) setInventoryEditor(null);
      return nextSystems;
    } catch (error) {
      console.error('Error saving inventory item:', error);
      showToast('Could not save that item. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeInventoryItem = async (systemType) => {
    setLoading(true);
    try {
      await homeownerRecords({
        resource: 'client_systems',
        action: 'delete',
        systemType,
      });
      setSystemsData((prev) => {
        const next = { ...prev };
        delete next[systemType];
        return next;
      });
    } catch (error) {
      console.error('Error removing inventory item:', error);
      showToast('Could not remove that item. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const labelForType = (id) => (INVENTORY_TYPES.find((type) => type.id === id) || {}).label || 'Item';

  const finishFromForm = async (draft) => {
    let systems = systemsData;
    if (draft && draft.systemType) {
      try {
        systems = await saveInventoryItem(draft);
      } catch (error) {
        return;
      }
    }
    await saveRobustSetup(undefined, systems, { skipped: false });
  };

  const skipSetup = () => saveRobustSetup(undefined, systemsData, { skipped: true });

  // Step 1: Welcome
  if (step === 1) {
    return (
      <SetupScreen>
        {onLogout && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button
              type="button"
              onClick={onLogout}
              style={{
                background: 'transparent',
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                padding: '0.5rem 1rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Sign out
            </button>
          </div>
        )}
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.5rem', color: brandColor, marginBottom: '1rem' }}>
            Welcome to Pacarion!
          </h1>
          <p style={{ fontSize: '1.2rem', color: colors.text, marginBottom: '2rem' }}>
            Let's set up your home maintenance dashboard in just a few minutes.
          </p>
          <button
            type="button"
            onClick={() => setStep(2)}
            style={{
              background: `linear-gradient(135deg, ${brandColor} 0%, #4ab3b3 100%)`,
              color: 'white',
              border: 'none',
              padding: '1rem 3rem',
              borderRadius: '0.5rem',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: `0 4px 12px rgba(89, 196, 196, 0.3)`
            }}
          >
            Get Started
          </button>
        </div>
      </SetupScreen>
    );
  }

  // Step 2: Property Address
  if (step === 2 && showHomeQuestions) {
    return (
      <SetupScreen>
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <HomeProfileQuestions
            mode="wizard"
            value={propertyData}
            onChange={setPropertyData}
            brandColor={brandColor}
            onFinish={() => {
              setShowHomeQuestions(false);
              setSetupType('robust');
              setStep(4);
            }}
            onSkip={() => {
              setShowHomeQuestions(false);
              setSetupType('robust');
              setStep(4);
            }}
          />
        </div>
      </SetupScreen>
    );
  }

  if (step === 2) {
    return (
      <SetupScreen>
        {onLogout && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button
              type="button"
              onClick={onLogout}
              style={{
                background: 'transparent',
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                padding: '0.5rem 1rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Sign out
            </button>
          </div>
        )}
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ color: colors.text, marginBottom: '1.5rem' }}>What's your property address?</h2>

          <input
            type="text"
            placeholder="123 Main St, City, FL 12345"
            value={propertyData.address}
            onChange={(e) => setPropertyData({ ...propertyData, address: e.target.value })}
            style={{
              width: '100%',
              padding: '1rem',
              border: `2px solid ${colors.border}`,
              borderRadius: '0.5rem',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              background: colors.surface,
              color: colors.text
            }}
          />

          <label style={{ display: 'block', color: colors.text, marginBottom: '0.5rem', fontWeight: '600' }}>
            Property Type
          </label>
          <select
            value={propertyData.property_type}
            onChange={(e) => setPropertyData({ ...propertyData, property_type: e.target.value })}
            style={{
              width: '100%',
              padding: '1rem',
              border: `2px solid ${colors.border}`,
              borderRadius: '0.5rem',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              background: colors.surface,
              color: colors.text
            }}
          >
            {propertyTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>

          <p style={{ color: colors.textSecondary, marginTop: 0 }}>
            Next you will tap what the house is made of. One question at a time.
          </p>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={() => setStep(1)}
              style={{
                flex: 1,
                padding: '1rem',
                border: `2px solid ${colors.border}`,
                borderRadius: '0.5rem',
                background: 'transparent',
                color: colors.text,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setShowHomeQuestions(true)}
              disabled={!propertyData.address}
              style={{
                flex: 1,
                padding: '1rem',
                background: propertyData.address ? `linear-gradient(135deg, ${brandColor} 0%, #4ab3b3 100%)` : colors.border,
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: propertyData.address ? 'pointer' : 'not-allowed',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </SetupScreen>
    );
  }

  // Step 4: add or edit one item. Typed values save only when Add item is pressed.
  if (step === 4 && setupType === 'robust') {
    if (inventoryEditor) {
      const editingType = inventoryEditor.systemType || '';
      const addedItems = Object.keys(systemsData).map((id) => {
        const data = systemsData[id] || {};
        const brand = data.manufacturer || data.brand || '';
        const year = data.install_year || data.purchase_year || '';
        return {
          id,
          label: labelForType(id),
          detail: [brand, year].filter(Boolean).join(' · '),
        };
      });
      return (
        <InventoryItemForm
          colors={colors}
          features={propertyData.features}
          existingData={editingType ? (systemsData[editingType] || {}) : null}
          initialType={editingType}
          saving={loading}
          resetAfterSave={!editingType}
          items={editingType ? undefined : addedItems}
          onRemove={editingType ? undefined : removeInventoryItem}
          onToggleFeature={handleFeatureToggle}
          onCancel={() => {
            if (editingType || startAtSystemsStep) {
              setInventoryEditor(null);
              return;
            }
            setStep(2);
          }}
          onSave={saveInventoryItem}
          onDone={editingType ? undefined : finishFromForm}
          onSkip={editingType ? undefined : skipSetup}
        />
      );
    }

    const savedIds = Object.keys(systemsData);
    const wideButton = {
      width: '100%',
      minHeight: '52px',
      borderRadius: '0.9rem',
      fontSize: '1.05rem',
      fontWeight: 700,
      cursor: loading ? 'not-allowed' : 'pointer',
    };
    return (
      <SetupScreen>
        {onLogout && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
            <button
              type="button"
              onClick={onLogout}
              style={{
                background: 'transparent',
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                minHeight: '44px',
                padding: '0.5rem 1rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Sign out
            </button>
          </div>
        )}
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.35rem' }}>Home inventory</h2>
          <p style={{ margin: '0 0 1.25rem', color: colors.textSecondary }}>
            Add an item when you are ready. Tap one you already saved to edit just that item.
          </p>
          <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1.25rem' }}>
            {savedIds.length === 0 && (
              <p style={{ margin: 0, color: colors.textSecondary }}>No items yet.</p>
            )}
            {savedIds.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setInventoryEditor({ systemType: id, data: systemsData[id] || {} })}
                style={{
                  minHeight: '52px',
                  textAlign: 'left',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.75rem',
                  border: `1px solid ${colors.border}`,
                  background: colors.surface,
                  color: colors.text,
                  fontWeight: 650,
                  fontSize: '1rem',
                }}
              >
                {labelForType(id)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setInventoryEditor({ systemType: '', data: null })}
            style={{ ...wideButton, border: 'none', background: brandColor, color: '#083838', marginBottom: '0.75rem' }}
          >
            Add item
          </button>
          <button
            type="button"
            onClick={() => (startAtSystemsStep ? saveRobustSetup() : setStep(5))}
            disabled={loading}
            style={{ ...wideButton, border: 'none', background: loading ? colors.border : brandColor, color: '#083838', marginBottom: '0.75rem' }}
          >
            {loading ? 'Saving...' : 'Finish setup'}
          </button>
          <button
            type="button"
            onClick={() => {
              if (onCancel) onCancel();
              else setStep(2);
            }}
            style={{ ...wideButton, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.text }}
          >
            {onCancel ? 'Cancel' : 'Back'}
          </button>
        </div>
      </SetupScreen>
    );
  }

  if (step === 5) {
    const savePlan = (fields) => saveRobustSetup(fields);
    return (
      <SetupScreen>
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.35rem' }}>A few home details</h2>
          <HomeFactsFields
            value={propertyData}
            onChange={setPropertyData}
            brandColor={brandColor}
          />
          <div style={{ height: '1.25rem' }} />
          <HomeownerPlanStep
            saving={loading}
            tasksReady={tasksReady}
            brandColor={brandColor}
            hideRoofAge
            hideFilter
            hideAcAge
            onSave={savePlan}
            onSkip={() => savePlan({ onboarding_completed_at: new Date().toISOString() })}
          />
        </div>
      </SetupScreen>
    );
  }

  return null;
};

export default HomeownerOnboarding;
